import { createContext, useContext, useState } from 'react';
import * as PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { Hanko } from '@teamhanko/hanko-elements';

export type Logout = (url?: string) => void;
export type Login = (path?: string) => void;
export type HankoLoginCallback = () => void;
const LOGIN_PATH = '/login';

export interface Auth {
  logout: Logout;
  login: Login;
  hanko?: Hanko;
  hankoLoginCallback: HankoLoginCallback;
  jwt?: string;
}

export const AuthContext = createContext<Auth>({
  logout: () => {
    /* no initialized */
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  login: (path?: string) => {
    /* no initialized */
  },
  hanko: undefined,
  hankoLoginCallback: () => {
    /* no initialized */
  },
  jwt: undefined,
});

interface AuthProviderProps {
  children: React.ReactNode;
  authUrl: string;
  authCookie?: string;
  onLogout: () => void;
  useNavigate: () => (path: string) => void;
}

const useAuthContext = (): Auth => useContext(AuthContext);

// To be removed when hanko release new version
// This is a logout hack until hanko relases a logout endpoint to remove the cookie manually
export async function hankoLogout(authUrl: string, authCookie: string) {
  const isLocalHost = authUrl.includes('localhost');

  const domain = authUrl.includes('agrantis.com')
    ? '.agrantis.com'
    : '.agrantis.dev';

  isLocalHost
    ? Cookies.remove(authCookie)
    : Cookies.remove(authCookie, { path: '/', domain });

  await fetch(`${authUrl}/logout`, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

function AuthProvider({
  children,
  authUrl,
  onLogout,
  authCookie = 'hanko',
  useNavigate,
}: AuthProviderProps) {
  const [jwt, setJwt] = useState<string | undefined>(Cookies.get(authCookie));
  const navigate = useNavigate();
  const hanko = new Hanko(authUrl);
  const [redirectPath, setRedirectPath] = useState<string>();
  const logout: Logout = async (path = LOGIN_PATH) => {
    //
    console.log('logging out');
    await hankoLogout(authUrl, authCookie);
    onLogout();
    navigate(path);
  };

  const login: Login = (redirectPath = '/') => {
    setRedirectPath(redirectPath);
    navigate(LOGIN_PATH);
  };

  const hankoLoginCallback: HankoLoginCallback = () => {
    setJwt(Cookies.get(authCookie));
    if (redirectPath) {
      navigate(redirectPath);
    }
  };

  const auth: Auth = { logout, login, hanko, jwt, hankoLoginCallback };
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useAuthContext, AuthProvider };
